import { createMachine, assign, InterpreterFrom, sendParent } from "xstate-4";
import { xlog } from "vue-app/utils/xstate";
import axios from "vue-app/utils/axios";

type Context = {
  text: string;
  responseImproved: string;
  generationId: string;
  conversationId: number;
};

type AiImproveMyResponse = {
  generationId: string;
  responseImproved: string;
};

export const machineInboxImproveMyResponse = createMachine(
  {
    id: "InboxImproveMyResponse",
    tsTypes: {} as import("./machineInboxImproveMyResponse.typegen").Typegen0,
    schema: {
      events: {} as { type: "CANCEL" } | { type: "APPLY" } | { type: "RETRY" },
      context: {} as Context,
      services: {} as {
        getImprovedText: {
          data: AiImproveMyResponse;
        };
      },
    },
    context: {
      text: "",
      responseImproved: "",
      generationId: "",
      conversationId: 0,
    },
    initial: "loading",
    states: {
      loading: {
        tags: ["loading"],
        invoke: {
          id: "getImprovedText",
          src: "getImprovedText",
          onDone: {
            target: "idle",
            actions: ["assignResponseImproved", "assignGenerationId"],
          },
          onError: {
            target: "error",
          },
        },
      },
      idle: {
        tags: ["idle"],
        always: {
          target: "noChanges",
          cond: "responseImprovedIsSameAsOriginal",
        },
        on: {
          APPLY: {
            actions: [
              "reportResponseImprovedOnApply",
              sendParent((context) => ({
                type: "APPLY_IMPROVE_TEXT",
                message: removeTags(context.responseImproved),
              })),
            ],
          },
        },
      },
      error: {
        tags: ["error"],
        on: {
          RETRY: ["loading"],
          CANCEL: {
            actions: [
              "reportResponseImprovedOnCancelFromErrorState",
              sendParent(() => ({ type: "CLOSE_IMPROVE_MY_RESPONSE_MODAL" })),
            ],
          },
        },
      },
      noChanges: {
        tags: ["noChanges"],
        on: {
          CANCEL: {
            actions: [
              "reportResponseImprovedOnCancelFromNoChangesState",
              sendParent(() => ({ type: "CLOSE_IMPROVE_MY_RESPONSE_MODAL" })),
            ],
          },
        },
      },
    },
    on: {
      CANCEL: {
        actions: ["reportResponseImprovedOnCancel", sendParent(() => ({ type: "CLOSE_IMPROVE_MY_RESPONSE_MODAL" }))],
      },
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      assignResponseImproved: assign({
        responseImproved: (context, event) => {
          xlog({ label: "assignResponseImproved", type: "action", context, event });
          return event.data.responseImproved;
        },
      }),
      assignGenerationId: assign({
        generationId: (context, event) => {
          xlog({ label: "assignGenerationId", type: "action", context, event });
          return event.data.generationId;
        },
      }),
      reportResponseImprovedOnApply: (context, event) => {
        xlog({ label: "reportResponseImprovedOnApply", type: "action", context, event });
        axios.post("/inbox/send-ai-improve-my-response-user-interaction", {
          inbox_conversation_id: context.conversationId,
          suggestion_status: "ACCEPTED",
          generation_id: context.generationId,
        });
      },
      reportResponseImprovedOnCancelFromNoChangesState: (context, event) => {
        xlog({ label: "reportResponseImprovedOnCancelFromNoChangesState", type: "action", context, event });
        axios.post("/inbox/send-ai-improve-my-response-user-interaction", {
          inbox_conversation_id: context.conversationId,
          suggestion_status: "NO_CHANGE",
          generation_id: context.generationId,
        });
      },
      reportResponseImprovedOnCancelFromErrorState: (context, event) => {
        xlog({ label: "reportResponseImprovedOnCancelFromErrorState", type: "action", context, event });
        axios.post("/inbox/send-ai-improve-my-response-user-interaction", {
          inbox_conversation_id: context.conversationId,
          suggestion_status: "REJECTED",
          generation_id: context.generationId || "rejected-after-error",
        });
      },
      reportResponseImprovedOnCancel: (context, event) => {
        xlog({ label: "reportResponseImprovedOnCancel", type: "action", context, event });
        axios.post("/inbox/send-ai-improve-my-response-user-interaction", {
          inbox_conversation_id: context.conversationId,
          suggestion_status: "REJECTED",
          generation_id: context.generationId || "rejected-before-generation",
        });
      },
    },
    guards: {
      responseImprovedIsSameAsOriginal: (context, event) => {
        xlog({ label: "responseImprovedIsSameAsOriginal", type: "guard", context, event });
        return context.responseImproved === context.text;
      },
    },
    services: {
      getImprovedText: async (context, event) => {
        xlog({ label: "getImprovedText", context, event });
        const response = await axios.post("/inbox/create-ai-improve-my-response", {
          inbox_conversation_id: context.conversationId,
          user_query: context.text,
        });
        return response.data.data;
      },
    },
  }
);

function removeTags(text: string) {
  return text.replace(/<[^>]*>?/gm, "");
}

export type InboxImproveMyResponseActor = InterpreterFrom<typeof machineInboxImproveMyResponse>;
